import type { CatalogSort } from '../models/CatalogSort';
import {
  decodeIndex,
  encodeIndex,
  itemsMatching,
  newScoreItem,
  titleFromFileName,
  uniqueFileName,
  type ScoreItem,
} from '../models/ScoreItem';
import { kindOfFile, type ScoreKind } from '../models/ScoreKind';
import { RecognitionService } from '../recognition/RecognitionService';
import { statusLine, type RunState } from '../recognition/progress';
import {
  INDEX_KEY,
  LocalStore,
  QuotaExceeded,
  blobKey,
  type CommitOp,
} from '../storage/localStore';

export { QuotaExceeded };

/**
 * The catalog, straight across from Singline/Sources/Models/CatalogStore.swift
 * and ADR 0004: blobs in IndexedDB, `catalog.json` as the index, no server —
 * the list is small and JSON is easy to inspect and migrate.
 */
export class CatalogStore {
  private items: ScoreItem[] = [];
  /** Transient per-item progress line ("staff 7/12 · 4 threads · 1:03 · ~40s left"); not persisted. */
  private recognitionProgress: Record<string, string> = {};
  private usage = 0;
  private readonly listeners = new Set<() => void>();
  private readonly inFlight = new Set<string>();

  constructor(
    readonly storage: LocalStore = new LocalStore(),
    private readonly recognition: RecognitionService = new RecognitionService(),
    private readonly scoresBase: string = './player/scores',
  ) {}

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  snapshot(): {
    items: ScoreItem[];
    recognitionProgress: Record<string, string>;
    usageBytes: number;
  } {
    return {
      items: this.items.slice(),
      recognitionProgress: { ...this.recognitionProgress },
      usageBytes: this.usage,
    };
  }

  async load(): Promise<void> {
    const text = await this.storage.getItem(INDEX_KEY);
    if (text) {
      this.items = decodeIndex(text).map((item) =>
        // A recognition interrupted by a killed tab must not stay "pending" forever.
        item.recognition.state === 'pending' ? { ...item, recognition: { state: 'none' } } : item,
      );
    }
    this.usage = await this.storage.usageBytes();
    this.emit();
  }

  async seedIfNeeded(): Promise<void> {
    if (this.items.length > 0) return;
    const names = await this.listSeedScores();
    const imported: ScoreItem[] = [];
    const taken = new Set<string>();
    const ops: CommitOp[] = [];
    for (const name of names) {
      const fileName = uniqueFileName(name, taken);
      taken.add(fileName);
      const blob = await this.fetchSeed(name);
      if (!blob) continue;
      ops.push({ type: 'setBlob', key: blobKey(fileName), blob });
      imported.push(newScoreItem({
        title: titleFromFileName(name),
        fileName,
        kind: 'musicxml',
      }));
    }
    if (imported.length === 0) return;
    this.items = imported;
    ops.push({ type: 'setItem', key: INDEX_KEY, value: encodeIndex(this.items) });
    await this.storage.commit(ops);
    this.usage = await this.storage.usageBytes();
    this.emit();
  }

  async importFiles(files: Iterable<File>): Promise<number> {
    const incoming = [...files];
    if (incoming.length === 0) return 0;
    const imported: ScoreItem[] = [];
    const taken = new Set(this.items.map((item) => item.fileName));
    const ops: CommitOp[] = [];
    for (const file of incoming) {
      const kind = kindOfFile(file);
      if (!kind) continue;
      const fileName = uniqueFileName(file.name || fallbackName(kind), taken);
      taken.add(fileName);
      ops.push({ type: 'setBlob', key: blobKey(fileName), blob: file });
      imported.push(newScoreItem({
        title: titleFromFileName(fileName),
        fileName,
        kind,
      }));
    }
    if (imported.length === 0) return 0;
    const next = this.items.concat(imported);
    ops.push({ type: 'setItem', key: INDEX_KEY, value: encodeIndex(next) });
    await this.storage.commit(ops);
    this.items = next;
    this.usage = await this.storage.usageBytes();
    this.emit();
    for (const item of imported) {
      if (item.kind !== 'musicxml') this.recognize(item);
    }
    return imported.length;
  }

  async addCameraImage(source: Blob): Promise<void> {
    const blob = await jpegIfPossible(source);
    const stamp = formatStamp(new Date());
    const taken = new Set(this.items.map((item) => item.fileName));
    const fileName = uniqueFileName(`IMG_${stamp}.jpg`, taken);
    const item = newScoreItem({
      title: `Scan ${formatDisplayDateTime(new Date())}`,
      fileName,
      kind: 'image',
    });
    const next = this.items.concat(item);
    await this.storage.commit([
      { type: 'setBlob', key: blobKey(fileName), blob },
      { type: 'setItem', key: INDEX_KEY, value: encodeIndex(next) },
    ]);
    this.items = next;
    this.usage = await this.storage.usageBytes();
    this.emit();
    this.recognize(item);
  }

  async delete(item: ScoreItem): Promise<void> {
    const next = this.items.filter((candidate) => candidate.id !== item.id);
    await this.storage.commit([
      { type: 'remove', key: blobKey(item.fileName) },
      { type: 'setItem', key: INDEX_KEY, value: encodeIndex(next) },
    ]);
    this.items = next;
    delete this.recognitionProgress[item.id];
    this.usage = await this.storage.usageBytes();
    this.emit();
  }

  async xmlText(item: ScoreItem): Promise<string | null> {
    if (item.kind !== 'musicxml') return null;
    const blob = await this.storage.getBlob(blobKey(item.fileName));
    return blob ? blob.text() : null;
  }

  itemsMatching(query: string, sort: CatalogSort): ScoreItem[] {
    return itemsMatching(this.items, query, sort);
  }

  /**
   * Starts (or restarts, e.g. after a failure) recognition of a PDF/image item.
   *
   * On success the produced MusicXML is stored as `<title>.musicxml` in the
   * catalog, a new MusicXML item is created and the source PDF/image is removed
   * — record and blob both, ADR 0004. On failure the source is marked failed.
   */
  recognize(item: ScoreItem): void {
    if (item.kind === 'musicxml') return;
    const current = this.items.find((candidate) => candidate.id === item.id);
    if (!current || current.recognition.state === 'pending') return;
    if (this.inFlight.has(item.id)) return;
    this.inFlight.add(item.id);
    void this.runRecognition(current);
  }

  /**
   * A page takes minutes, so the line under a pending item has to say what is
   * happening rather than only that something is: which staff is being read,
   * how many threads the module settled on, how long this page has run and
   * roughly what is left (recognition/progress.ts).
   *
   * The clock ticks on a timer because the pipeline only speaks when it
   * finishes a stage — and step 10 on a dense page can be quiet for a while.
   * The service's callbacks are claimed for the length of this run and dropped
   * after it, so the line belongs to the item that is actually running.
   */
  private async runRecognition(item: ScoreItem): Promise<void> {
    await this.setRecognition({ state: 'pending' }, item.id);
    const run: RunState = { page: 1, pages: 1, elapsed: 0, finished: [] };
    let pageStartedAt = Date.now();
    const show = (): void => {
      run.elapsed = (Date.now() - pageStartedAt) / 1000;
      this.recognitionProgress[item.id] = statusLine(run);
      this.emit();
    };
    const ticker = setInterval(show, 1000);

    this.recognition.onActivity = (activity) => {
      if (activity.kind === 'started') {
        run.threads = activity.threads;
      } else {
        run.stage = activity.stage;
        run.current = activity.current;
        run.total = activity.total;
        // Where the projection is anchored: from here it counts down until the
        // pipeline speaks again (recognition/progress.ts).
        run.markedElapsed = (Date.now() - pageStartedAt) / 1000;
      }
      show();
    };
    // Only the seconds: the next page starts its own clock from the progress
    // callback below, which fires before the page is rasterised.
    this.recognition.onRun = (report) => run.finished.push(report.secs);

    try {
      const source = await this.storage.getBlob(blobKey(item.fileName));
      if (!source) throw new Error('Could not open the file.');
      const xml = item.kind === 'pdf'
        ? await this.recognition.recognizePdf(source, (page, total) => {
          run.page = page;
          run.pages = total;
          run.stage = undefined;
          run.current = undefined;
          run.total = undefined;
          run.markedElapsed = undefined;
          pageStartedAt = Date.now();
          show();
        })
        : await this.recognition.recognizeImage(source);
      await this.finishRecognition(item.id, item.title, xml);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown recognition error.';
      await this.failRecognition(item.id, message);
    } finally {
      clearInterval(ticker);
      this.recognition.onActivity = undefined;
      this.recognition.onRun = undefined;
      this.inFlight.delete(item.id);
    }
  }

  private async finishRecognition(sourceId: string, title: string, xml: string): Promise<void> {
    delete this.recognitionProgress[sourceId];
    const taken = new Set(this.items.map((item) => item.fileName));
    const fileName = uniqueFileName(`${title}.musicxml`, taken);
    const source = this.items.find((item) => item.id === sourceId);
    const next = this.items.filter((item) => item.id !== sourceId).concat(
      newScoreItem({ title, fileName, kind: 'musicxml' }),
    );
    const ops: CommitOp[] = [
      { type: 'setBlob', key: blobKey(fileName), blob: new Blob([xml], { type: 'application/xml' }) },
      { type: 'setItem', key: INDEX_KEY, value: encodeIndex(next) },
    ];
    if (source) ops.push({ type: 'remove', key: blobKey(source.fileName) });
    try {
      await this.storage.commit(ops);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not save the result.';
      await this.failRecognition(sourceId, message);
      return;
    }
    this.items = next;
    this.usage = await this.storage.usageBytes();
    this.emit();
  }

  private async failRecognition(sourceId: string, message: string): Promise<void> {
    delete this.recognitionProgress[sourceId];
    await this.setRecognition({ state: 'failed', message }, sourceId);
  }

  private async setRecognition(state: ScoreItem['recognition'], id: string): Promise<void> {
    this.items = this.items.map((item) => (item.id === id ? { ...item, recognition: state } : item));
    this.emit();
    await this.persistIndex();
  }

  private async persistIndex(): Promise<void> {
    try {
      await this.storage.setItem(INDEX_KEY, encodeIndex(this.items));
      this.usage = await this.storage.usageBytes();
    } catch {
      // A failed index write leaves the in-memory list as the source of truth
      // until the next successful commit; the UI still has to move.
    }
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private async listSeedScores(): Promise<string[]> {
    try {
      const response = await fetch(`${this.scoresBase}/index.json`);
      if (!response.ok) return [];
      const parsed: unknown = await response.json();
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((name): name is string => typeof name === 'string')
        .filter((name) => name.toLowerCase().endsWith('.musicxml'))
        .sort((a, b) => a.localeCompare(b));
    } catch {
      return [];
    }
  }

  private async fetchSeed(name: string): Promise<Blob | null> {
    try {
      const response = await fetch(`${this.scoresBase}/${encodeURIComponent(name)}`);
      if (!response.ok) return null;
      return await response.blob();
    } catch {
      return null;
    }
  }
}

function fallbackName(kind: ScoreKind): string {
  switch (kind) {
    case 'musicxml':
      return 'untitled.musicxml';
    case 'pdf':
      return 'untitled.pdf';
    case 'image':
      return 'untitled.jpg';
  }
}

function formatStamp(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

function formatDisplayDateTime(date: Date): string {
  return date.toLocaleString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

async function jpegIfPossible(source: Blob): Promise<Blob> {
  if (source.type === 'image/jpeg') return source;
  try {
    const bitmap = await createImageBitmap(source);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return source;
    ctx.drawImage(bitmap, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((next) => resolve(next), 'image/jpeg', 0.9);
    });
    return blob ?? source;
  } catch {
    return source;
  }
}
